
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabaseClient';

interface Job {
  id: string;
  service_category: string;
  service_subcategory: string;
  status: string;
  scheduled_date: string;
  customer_address: string;
  provider_name: string;
  completed_at: string;
  final_price: number;
  payment_status: string;
  has_review: boolean;
}

export default function JobHistory() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchJobHistory();
  }, [filter]);

  const fetchJobHistory = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      let query = supabase
        .from('jobs')
        .select(`
          *,
          provider_profiles(full_name),
          reviews(id)
        `)
        .eq('customer_id', user.id)
        .in('status', ['completed', 'cancelled', 'disputed'])
        .order('completed_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;

      if (error) throw error;

      const formattedJobs = data?.map((job: any) => ({
        ...job,
        provider_name: job.provider_profiles?.full_name || 'N/A',
        has_review: job.reviews && job.reviews.length > 0
      })) || [];

      setJobs(formattedJobs);
    } catch (error) {
      console.error('Error fetching job history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'disputed': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin"></i>
      </div>
    );
  }

  return (
    <div>
      {/* Filter Tabs */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex gap-2 overflow-x-auto">
          {['all', 'completed', 'cancelled', 'disputed'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                filter === f
                  ? 'bg-[#FF6A00] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {jobs.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-history-line text-4xl text-gray-400"></i>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Job History</h3>
          <p className="text-gray-600">Your completed jobs will appear here</p>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <div key={job.id} className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-gray-900">{job.service_subcategory}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(job.status)}`}>
                      {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm">{job.service_category}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">£{job.final_price || 0}</p>
                  {job.payment_status === 'paid' ? (
                    <span className="text-xs text-green-600 font-semibold">
                      <i className="ri-check-line"></i> Paid
                    </span>
                  ) : job.payment_status === 'pending' ? (
                    <span className="text-xs text-orange-600 font-semibold">
                      <i className="ri-time-line"></i> Payment Due
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <i className="ri-user-line text-blue-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Provider</p>
                    <p className="font-semibold text-gray-900">{job.provider_name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-calendar-line text-green-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Completed</p>
                    <p className="font-semibold text-gray-900">
                      {job.completed_at 
                        ? new Date(job.completed_at).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })
                        : 'N/A'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <i className="ri-map-pin-line text-purple-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Location</p>
                    <p className="font-semibold text-gray-900 text-sm">{job.customer_address}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <button
                  onClick={() => navigate(`/job-tracking/${job.id}`)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer font-semibold"
                >
                  <i className="ri-eye-line mr-2"></i>
                  View Details
                </button>
                
                {job.status === 'completed' && job.payment_status === 'pending' && (
                  <button
                    onClick={() => navigate(`/job-tracking/${job.id}?action=pay`)}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer font-semibold"
                  >
                    <i className="ri-secure-payment-line mr-2"></i>
                    Pay Invoice
                  </button>
                )}

                {job.status === 'completed' && !job.has_review && (
                  <button
                    onClick={() => navigate(`/job-tracking/${job.id}?action=review`)}
                    className="flex-1 px-4 py-2 bg-[#FF6A00] text-white rounded-lg hover:bg-[#FF8533] transition-colors whitespace-nowrap cursor-pointer font-semibold"
                  >
                    <i className="ri-star-line mr-2"></i>
                    Leave Review
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
